import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  AcademyEntitySet,
  ODataList,
} from '../entities/academy-entities.model';
import { ODataQuery } from '../entities/academy-query.model';
import { fromWireEnums } from '../entities/academy-enums.model';

/**
 * **Read-only** helper for the academy OData controller. The backend serves OData
 * **list** under `GET /odata/{Set}` (`ODataController`, `[EnableQuery]` —
 * `$filter`/`$orderby`/`$top`/`$skip`) at base `apiUrl.odata_academy_service`. The
 * `/odata` routes are GET-only.
 *
 * Mutations and by-id reads are a different surface entirely — the per-entity
 * `GenericController` at the plain root `/{Set}` — and live in the sibling
 * {@link AcademyCrudService}. This service intentionally exposes **only** `list`:
 * routing a `POST`/`PATCH`/`DELETE` at the `/odata` base would hit the read-only
 * controller and never reach the writer.
 */
@Injectable({ providedIn: 'root' })
export class AcademyODataService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.odata_academy_service;

  /**
   * `GET /odata/{Set}` — full OData list off the read-only `ODataController`. The
   * controller answers with the standard `value` envelope (also tolerates a bare
   * array); both shapes normalize into `ODataList`. Enum fields arrive as numeric
   * wire codes and are translated back to names (`fromWireEnums`).
   *
   * `context` is for the caller that has to mark the request — a background read passes
   * `withoutForbiddenElevation()`, so a 403 does not open the elevation modal over a page the
   * user did nothing on.
   */
  public list<T>(
    set: AcademyEntitySet,
    query: ODataQuery = {},
    context?: HttpContext
  ): Observable<ODataList<T>> {
    return this.http
      .get<ODataList<T> | T[]>(`${this.baseUrl}/${set}`, {
        params: this.buildODataParams(query),
        withCredentials: true,
        ...(context ? { context } : {}),
      })
      .pipe(map((res) => this.toList(res)));
  }

  /** Fold both wire shapes (envelope / bare array) into a name-enum `ODataList`. */
  private toList<T>(res: ODataList<T> | T[] | null): ODataList<T> {
    if (Array.isArray(res)) return { value: fromWireEnums(res) };
    return { ...(res ?? {}), value: fromWireEnums(res?.value ?? []) };
  }

  /** Build OData `$`-prefixed params, only for present options. */
  private buildODataParams(query: ODataQuery): HttpParams {
    let params = new HttpParams();
    if (query.filter) params = params.set('$filter', query.filter);
    if (query.select) params = params.set('$select', query.select);
    if (query.expand) params = params.set('$expand', query.expand);
    if (query.orderby) params = params.set('$orderby', query.orderby);
    if (query.top != null) params = params.set('$top', String(query.top));
    if (query.skip != null) params = params.set('$skip', String(query.skip));
    if (query.count) params = params.set('$count', 'true');
    return params;
  }
}
